import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useBreakpoint } from '@/design-system/hooks/useBreakpoint';
import { Btn } from '@/design-system';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Ico, L } from '@/design-system/icons';
import { motion } from '@/design-system/motion';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

// registerType 'prompt' sans UI = le nouveau service worker restait en attente
// jusqu'à fermeture complète de l'app : les clients gardaient l'ancien bundle
// indéfiniment. Montée à la racine (main.tsx). « Mettre à jour » déclenche
// skipWaiting + reload via updateServiceWorker(true).
export function PwaUpdatePrompt() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Une PWA/onglet reste ouvert des jours ; sans revérification périodique,
      // une nouvelle version n'est découverte qu'à la navigation suivante.
      if (registration) {
        setInterval(() => void registration.update(), UPDATE_CHECK_INTERVAL_MS);
      }
    },
  });

  // Décision Pablo (2026-07-28) : la bannière est réservée au MOBILE (PWA
  // installée / navigation tactile). Sur desktop, le service worker se met à
  // jour au cycle naturel (fermeture des onglets).
  const { isMobile } = useBreakpoint();
  if (!needRefresh || dismissed || !isMobile) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: isMobile ? 72 : 24,
        left: isMobile ? 12 : 24,
        right: isMobile ? 12 : undefined,
        // Sous les toasts (2000) : un toast d'action doit rester lisible.
        zIndex: 1900,
        display: 'flex',
        justifyContent: isMobile ? 'center' : 'flex-start',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          padding: '12px 14px',
          background: P.s2,
          border: `1px solid ${P.border}`,
          borderLeft: `3px solid ${P.accent}`,
          borderRadius: 8,
          maxWidth: 420,
          animation: `cp-slide-in-bottom ${motion.duration.normal} ${motion.easing.decelerate}`,
        }}
      >
        <Ico paths={[...L.download]} size={18} color={P.accent} sw={2} />
        <span
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 600,
            color: P.t1,
            lineHeight: 1.4,
            flex: 1,
            minWidth: 140,
          }}
        >
          {t('pwa.updateAvailable')}
        </span>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Btn variant="ghost" small label={t('pwa.updateLater')} onClick={() => setDismissed(true)} />
          <Btn
            variant="primary"
            small
            label={t('pwa.updateNow')}
            onClick={() => void updateServiceWorker(true)}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
